import { saveAs } from 'file-saver'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { BookType } from 'xlsx'

import { Add } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, LinearProgress, MenuItem, Stack, TextField, Typography } from '@mui/material'

import { useGetTopGamesWithAppIdsQuery } from '../../../api/top-grossing'
import { ExportDataButton } from '../../../components/ExportDataButton/ExportDataButton'
import { generateExport } from '../../../features/export-data/util/workbook'
import usePage from '../../../hooks/usePage'
import appStoreService from '../../../services/AppStoreService'
import pageService from '../../../services/PageService'
import { useLazyGetAdDataAggregateDebugQuery, useLazySearchAdDataAggregatesQuery } from '../../api/ad-data-tester'
import { useGetInternalConventionalItemsQuery } from '../../api/conventional-genres'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { AdDataAnalysisDialog } from '../../features/ad-data-tester/components/AdDataAnalysisDialog/AdDataAnalysisDialog'
import { AdDataResultsTable } from '../../features/ad-data-tester/components/AdDataResultsTable/AdDataResultsTable'
import { AdDataSearchForm } from '../../features/ad-data-tester/components/AdDataSearchForm/AdDataSearchForm'
import { NewMetadataAnalysisDialog } from '../../features/ad-data-tester/components/NewMetadataAnalysisDialog/NewMetadataAnalysisDialog'
import {
  adDataGameFields,
  AdDataGame,
  AdDataRow,
  buildAdDataExportRows,
  buildAdDataRows,
  buildAdDataSearchRequest,
  emptyAdDataSearchForm,
  getAnalysedAppIds,
} from '../../features/ad-data-tester/components/helpers/adDataHelpers'
import { AdDataSearchForm as AdDataSearchFormValues } from '../../features/ad-data-tester/types/AdData'
import { ConventionalItemType } from '../../features/conventional-genres/types/InternalConventionalItem'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

const defaultMarketIso = 'us'

type SearchPromise = ReturnType<ReturnType<typeof useLazySearchAdDataAggregatesQuery>[0]>

export const AdDataTesterPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-ad-data-tester'))
  const { t } = useTranslation()
  const { notifyError } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const marketsQuery = useGetInternalMarketsQuery()
  const subgenresQuery = useGetInternalConventionalItemsQuery(ConventionalItemType.Subgenre)
  const [search, searchResult] = useLazySearchAdDataAggregatesQuery()
  const [getAggregateDebug] = useLazyGetAdDataAggregateDebugQuery()
  const [form, setForm] = useState<AdDataSearchFormValues>(emptyAdDataSearchForm)
  const [isCancelled, setIsCancelled] = useState(false)
  const [analysisRow, setAnalysisRow] = useState<AdDataRow>()
  const [isNewAnalysisOpen, setIsNewAnalysisOpen] = useState(false)
  const [downloadingAppId, setDownloadingAppId] = useState<number>()
  const searchPromise = useRef<SearchPromise>()

  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [marketsQuery.data])
  const requestedMarketIso = searchParams.get('market') ?? defaultMarketIso
  const marketIso = markets.length === 0 || markets.some((market) => market.iso === requestedMarketIso) ? requestedMarketIso : defaultMarketIso
  const marketName = markets.find((market) => market.iso === marketIso)?.name ?? marketIso.toUpperCase()
  const subgenres = useMemo(
    () => [...(subgenresQuery.data ?? [])].map(({ id, name }) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
    [subgenresQuery.data]
  )

  const isSearching = searchResult.isFetching
  const aggregates = isSearching || isCancelled || searchResult.isError ? undefined : searchResult.currentData
  const analysedAppIds = useMemo(() => getAnalysedAppIds(aggregates), [aggregates])
  const topGamesQuery = useGetTopGamesWithAppIdsQuery(
    { marketIso, appIds: analysedAppIds, include: adDataGameFields.join(',') },
    { skip: analysedAppIds.length === 0 }
  )
  const games = analysedAppIds.length === 0 ? undefined : (topGamesQuery.currentData as AdDataGame[] | undefined)
  const rows = useMemo(() => (aggregates && games ? buildAdDataRows(games, aggregates, subgenres) : []), [aggregates, games, subgenres])
  const hasAnswer = !!aggregates && (analysedAppIds.length === 0 || !!games)
  const unrankedCount = analysedAppIds.length - rows.length

  const changeMarket = (iso: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('market', iso)
    setSearchParams(next, { replace: true })
  }

  const runSearch = () => {
    setIsCancelled(false)
    searchPromise.current = search(buildAdDataSearchRequest(form))
  }

  const cancelSearch = () => {
    setIsCancelled(true)
    searchPromise.current?.abort()
  }

  const downloadJson = useCallback(
    async (row: AdDataRow) => {
      setDownloadingAppId(row.appId)
      try {
        const data = await getAggregateDebug(row.appId, true).unwrap()
        saveAs(new Blob([JSON.stringify(data)], { type: 'text/json;charset=utf-8;' }), `${row.appId}-subgenre-analysis.json`)
      } catch {
        notifyError(t('internal-service-management:ad_data_download_failed'))
      } finally {
        setDownloadingAppId(undefined)
      }
    },
    [getAggregateDebug, notifyError, t]
  )

  const exportRows = (format: BookType) =>
    generateExport(format, buildAdDataExportRows(rows, subgenres, appStoreService.appStoreGenreMap), 'ad-data', 'ad-data')

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
        <Stack direction="row" alignItems="center" gap={2}>
          <TextField
            select
            size="small"
            label={t('internal-common:market')}
            value={markets.length ? marketIso : ''}
            onChange={(event) => changeMarket(event.target.value)}
            sx={{ minWidth: 200 }}
          >
            {markets.map((market) => (
              <MenuItem key={market.iso} value={market.iso}>
                {market.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" gap={1}>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setIsNewAnalysisOpen(true)}>
            {t('internal-service-management:ad_data_new_analysis')}
          </Button>
          <ExportDataButton analyticsEventOrigin="Internal Ad Data Tester" onChooseFormat={exportRows} disabled={!hasAnswer || rows.length === 0} />
        </Stack>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <AdDataSearchForm value={form} subgenres={subgenres} isSearching={isSearching} onChange={setForm} onSearch={runSearch} />
          {isSearching && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              action={
                <Button color="inherit" size="small" onClick={cancelSearch}>
                  {t('internal-common:cancel')}
                </Button>
              }
            >
              {t('internal-service-management:ad_data_searching')}
              <LinearProgress sx={{ mt: 1 }} />
            </Alert>
          )}
        </CardContent>
      </Card>

      {isCancelled && <Alert severity="info">{t('internal-service-management:ad_data_search_cancelled')}</Alert>}
      {!isSearching && !isCancelled && searchResult.isError && <Alert severity="error">{t('internal-service-management:ad_data_search_failed')}</Alert>}
      {topGamesQuery.isError && analysedAppIds.length > 0 && <Alert severity="error">{t('internal-service-management:ad_data_ranks_failed')}</Alert>}

      {aggregates && !topGamesQuery.isError && (
        <Box>
          {hasAnswer && (
            <Typography textAlign="center" mb={2}>
              {t('internal-service-management:ad_data_found', { count: rows.length })}
              {unrankedCount > 0 && ` ${t('internal-service-management:ad_data_unranked', { count: unrankedCount, market: marketName })}`}
            </Typography>
          )}
          {(rows.length > 0 || !hasAnswer) && (
            <Card>
              <AdDataResultsTable
                rows={rows}
                isLoading={!hasAnswer}
                downloadingAppId={downloadingAppId}
                onDownloadJson={downloadJson}
                onShowAnalyses={setAnalysisRow}
              />
            </Card>
          )}
        </Box>
      )}

      <AdDataAnalysisDialog row={analysisRow} subgenres={subgenres} onClose={() => setAnalysisRow(undefined)} />
      <NewMetadataAnalysisDialog open={isNewAnalysisOpen} markets={markets} defaultMarketIso={defaultMarketIso} onClose={() => setIsNewAnalysisOpen(false)} />
    </Box>
  )
}
